import React from 'react';
import { Box, Grid, TextField, Typography, Alert, Chip } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';

interface Props {
  extracted: Record<string, unknown>;
  confirmed: Record<string, string>;
  onChange: (field: string, value: string) => void;
}

const FIELDS = [
  { key: 'full_name',     label: 'Full Name' },
  { key: 'doc_number',    label: 'Passport Number' },
  { key: 'nationality',   label: 'Nationality (3-letter ISO)' },
  { key: 'date_of_birth', label: 'Date of Birth (YYYY-MM-DD)' },
  { key: 'sex',           label: 'Sex (M / F)' },
  { key: 'expiry_date',   label: 'Expiry Date (YYYY-MM-DD)' },
  { key: 'issuing_country', label: 'Issuing Country' },
];

const ConfirmExtractedStep: React.FC<Props> = ({ extracted, confirmed, onChange }) => {
  const conf = (extracted.ocr_confidence as number) ?? 0;
  const confColor = conf >= 0.85 ? 'success' : conf >= 0.6 ? 'warning' : 'error';

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <VerifiedIcon color="primary" />
        <Typography variant="h6">Confirm Extracted Fields</Typography>
        <Chip
          size="small"
          label={`OCR confidence: ${Math.round(conf * 100)}%`}
          color={confColor}
          sx={{ ml: 'auto' }}
        />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Review the fields extracted from the passport. Edit any incorrect values before proceeding.
      </Alert>

      <Grid container spacing={2}>
        {FIELDS.map(({ key, label }) => (
          <Grid item xs={12} sm={6} key={key}>
            <TextField
              fullWidth
              label={label}
              value={confirmed[key] ?? (extracted[key] as string) ?? ''}
              onChange={(e) => onChange(key, e.target.value)}
              size="small"
              helperText={
                !extracted[key] ? '⚠ Not detected — please enter manually' : undefined
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        ))}
      </Grid>

      {extracted.mrz_line1 && (
        <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">MRZ (raw)</Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', wordBreak: 'break-all' }}>
            {extracted.mrz_line1 as string}
          </Typography>
          <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block', wordBreak: 'break-all' }}>
            {extracted.mrz_line2 as string}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default ConfirmExtractedStep;
